/**
 * SCADAPanel
 *
 * Slide-in right-side panel showing live SCADA context for an asset or region.
 * Mirrors the DatabricksPanel UX so it feels native, but uses orange/red
 * "operations" accents to signal its different domain.
 *
 * Two modes:
 *   - assetMode    asset_id passed in -> show full dossier
 *   - regionMode   filters passed in -> show region alert summary
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import '../styles/SCADAPanel.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const SEV_META = {
  critical: { label: 'CRITICAL', color: '#dc2626', bg: '#fef2f2' },
  warning:  { label: 'WARNING',  color: '#d97706', bg: '#fffbeb' },
  info:     { label: 'INFO',     color: '#2563eb', bg: '#eff6ff' },
  nominal:  { label: 'NOMINAL',  color: '#059669', bg: '#ecfdf5' },
};

function fmtRelativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function MiniSparkline({ values, color = '#dc2626', width = 240, height = 36 }) {
  if (!values || values.length === 0) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1 || 1);
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * height;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={width} height={height} className="scada-sparkline">
      <polyline fill="none" stroke={color} strokeWidth="1.6" points={points} />
      {values.map((v, i) => (
        <circle key={i} cx={i * stepX} cy={height - ((v - min) / range) * height} r="1.5" fill={color} />
      ))}
    </svg>
  );
}

function SCADAPanel({ mode, assetId, filters }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    if (!mode) return;
    setLoading(true); setError(null);
    try {
      let url;
      if (mode === 'asset' && assetId) {
        url = `${BACKEND_URL}/api/scada/asset/${encodeURIComponent(assetId)}`;
      } else if (mode === 'region') {
        const qs = new URLSearchParams();
        if (filters?.county)   qs.set('county', filters.county);
        if (filters?.basin)    qs.set('basin', filters.basin);
        if (filters?.severity) qs.set('severity', filters.severity);
        url = `${BACKEND_URL}/api/scada/region?${qs.toString()}`;
      } else {
        url = `${BACKEND_URL}/api/scada/alerts?severity=critical&limit=20`;
      }
      const res = await fetch(url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [mode, assetId, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const dossier   = mode === 'asset'  ? data : null;
  const region    = mode === 'region' ? data : null;

  // Telemetry: pick a primary metric per layer
  const telemetryMetric = useMemo(() => {
    if (!dossier?.telemetry?.length) return null;
    const sample = dossier.telemetry[0];
    const layer = dossier.layer;
    const map = {
      sources:   { key: 'flow_bwpd',       label: 'Flow (bwpd)',         color: '#2563eb' },
      disposal:  { key: 'injection_bwpd',  label: 'Injection (bwpd)',    color: '#dc2626' },
      pipelines: { key: 'throughput_bwpd', label: 'Throughput (bwpd)',   color: '#7c3aed' },
      treatment: { key: 'throughput_bwpd', label: 'Throughput (bwpd)',   color: '#059669' },
    }[layer];
    if (!map || !(map.key in sample)) return null;
    return { ...map, values: dossier.telemetry.map(p => p[map.key]) };
  }, [dossier]);

  const secondaryMetric = useMemo(() => {
    if (!dossier?.telemetry?.length) return null;
    const layer = dossier.layer;
    const sample = dossier.telemetry[0];
    const map = {
      sources:   { key: 'tds_ppm',        label: 'TDS (ppm)',         color: '#0891b2' },
      disposal:  { key: 'pressure_psi',   label: 'Pressure (psi)',    color: '#ea580c' },
      pipelines: { key: 'pressure_out_psi', label: 'Outlet Press (psi)', color: '#ea580c' },
      treatment: { key: 'recovery_pct',   label: 'Recovery (%)',      color: '#0891b2' },
    }[layer];
    if (!map || !(map.key in sample)) return null;
    return { ...map, values: dossier.telemetry.map(p => p[map.key]) };
  }, [dossier]);

  if (!mode) {
    return (
      <div className="scada-empty">
        <span className="scada-icon">⚡</span>
        <div>Run a SCADA query in chat to see operational context here.</div>
      </div>
    );
  }

  return (
    <div className="scada-panel">
      <div className="scada-panel-subheader">
        <div className="scada-subheader-text">
          {mode === 'asset' ? `Asset ${assetId}` : 'Regional view'}
          {filters?.county && ` · ${filters.county} County`}
          {filters?.severity && ` · ${filters.severity}`}
        </div>
        <button className="scada-refresh" onClick={fetchData} title="Refresh" aria-label="Refresh">↻</button>
      </div>

      <div className="scada-panel-body">
        {loading && <div className="scada-loading">Loading SCADA data…</div>}
        {error && <div className="scada-error">⚠ {error}</div>}

        {/* ─── ASSET DOSSIER ─── */}
        {dossier && dossier.alerts?.length > 0 && (
          <>
            <Section title="Asset">
              <div className="scada-asset-card">
                <div className="scada-asset-name">{dossier.asset_name}</div>
                <div className="scada-asset-meta">
                  {dossier.county} County · {dossier.operator} · {dossier.layer}
                </div>
                <div className="scada-status-row">
                  <SeverityBadge severity={dossier.status} />
                  <span className="scada-asset-id">#{dossier.asset_id}</span>
                </div>
              </div>
            </Section>

            <Section title={`Active Alerts (${dossier.alerts.length})`}>
              {dossier.alerts.map(a => (
                <div key={a.id} className="scada-alert-card" style={{ borderLeftColor: SEV_META[a.severity].color }}>
                  <div className="scada-alert-head">
                    <SeverityBadge severity={a.severity} />
                    <span className="scada-alert-code">{a.code}</span>
                    <span className="scada-alert-time">{fmtRelativeTime(a.first_seen_at)}</span>
                  </div>
                  <div className="scada-alert-title">{a.title}</div>
                  <div className="scada-alert-desc">{a.description}</div>
                </div>
              ))}
            </Section>

            {dossier.operational_impact && (
              <Section title="Operational Impact">
                <div className="scada-impact-card">
                  <div className="scada-impact-num">
                    {(dossier.operational_impact.capacity_offline_value || dossier.operational_impact.capacity_offline_bbl_day || 0).toLocaleString()}
                    <span className="scada-impact-unit"> {dossier.operational_impact.capacity_offline_unit || 'bbl/day'}</span>
                  </div>
                  <div className="scada-impact-label">capacity at risk</div>
                  <div className="scada-impact-summary">{dossier.operational_impact.summary}</div>
                </div>
              </Section>
            )}

            {(telemetryMetric || secondaryMetric) && (
              <Section title="Telemetry — Last 24h">
                {telemetryMetric && (
                  <div className="scada-metric">
                    <div className="scada-metric-head">
                      <span className="scada-metric-label">{telemetryMetric.label}</span>
                      <span className="scada-metric-now">
                        Now: {Math.round(telemetryMetric.values[telemetryMetric.values.length - 1]).toLocaleString()}
                      </span>
                    </div>
                    <MiniSparkline values={telemetryMetric.values} color={telemetryMetric.color} />
                  </div>
                )}
                {secondaryMetric && (
                  <div className="scada-metric">
                    <div className="scada-metric-head">
                      <span className="scada-metric-label">{secondaryMetric.label}</span>
                      <span className="scada-metric-now">
                        Now: {Math.round(secondaryMetric.values[secondaryMetric.values.length - 1]).toLocaleString()}
                      </span>
                    </div>
                    <MiniSparkline values={secondaryMetric.values} color={secondaryMetric.color} />
                  </div>
                )}
              </Section>
            )}

            {dossier.troubleshooting && (
              <Section title="Troubleshooting Playbook">
                <div className="scada-playbook">
                  <div className="scada-playbook-section">
                    <div className="scada-playbook-label">Likely causes</div>
                    <ul>
                      {dossier.troubleshooting.likely_causes.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                  <div className="scada-playbook-section">
                    <div className="scada-playbook-label">Recommended steps</div>
                    <ol>
                      {dossier.troubleshooting.steps.map((s, i) => <li key={i}>{s}</li>)}
                    </ol>
                  </div>
                  <div className="scada-playbook-meta">
                    <span>⏱ ~{dossier.troubleshooting.est_minutes} min</span>
                    {dossier.troubleshooting.parts.length > 0 && (
                      <span>· Parts: {dossier.troubleshooting.parts.join(', ')}</span>
                    )}
                  </div>
                </div>
              </Section>
            )}

            {dossier.nearest_crews?.length > 0 && (
              <Section title="Nearest Crews">
                <table className="scada-crew-table">
                  <thead>
                    <tr>
                      <th>Crew</th><th>Distance</th><th>ETA</th><th>Skill match</th><th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dossier.nearest_crews.map((c, i) => (
                      <tr key={c.id} className={i === 0 ? 'scada-crew-recommended' : ''}>
                        <td>
                          <div className="scada-crew-name">
                            {i === 0 && <span className="scada-rec-pill">Recommend</span>}
                            {c.name}
                          </div>
                          <div className="scada-crew-lead">{c.lead}</div>
                        </td>
                        <td>{c.distance_miles} mi</td>
                        <td>{c.eta_minutes} min</td>
                        <td>{'★'.repeat(Math.min(c.skill_match, 3))}{'☆'.repeat(Math.max(0, 3 - c.skill_match))}</td>
                        <td><span className={`scada-status-pill scada-status-${c.status}`}>{c.status}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Section>
            )}
          </>
        )}

        {dossier && dossier.alerts?.length === 0 && (
          <div className="scada-nominal">
            <SeverityBadge severity="nominal" />
            <div className="scada-nominal-msg">No active SCADA alerts for {assetId}.</div>
          </div>
        )}

        {/* ─── REGION SUMMARY ─── */}
        {region && (
          <>
            <Section title="Region Summary">
              <div className="scada-region-stats">
                <div className="scada-stat">
                  <div className="scada-stat-num">{region.total_alerts}</div>
                  <div className="scada-stat-label">Active alerts</div>
                </div>
                <div className="scada-stat">
                  <div className="scada-stat-num scada-stat-impact">
                    {Math.round((region.total_impact_value || region.total_impact_bbl_day || 0) / 1000)}K
                  </div>
                  <div className="scada-stat-label">{region.total_impact_unit || 'bbl/day'} at risk</div>
                </div>
                <div className="scada-stat">
                  <div className="scada-stat-num scada-stat-crit">
                    {region.by_severity?.critical || 0}
                  </div>
                  <div className="scada-stat-label">Critical</div>
                </div>
              </div>
              <div className="scada-region-breakdown">
                <div className="scada-region-row">
                  <span className="scada-region-label">By severity</span>
                  <span>
                    {Object.entries(region.by_severity || {}).map(([k, v]) => (
                      <span key={k} className="scada-pill" style={{ background: SEV_META[k].bg, color: SEV_META[k].color }}>
                        {k}: {v}
                      </span>
                    ))}
                  </span>
                </div>
                <div className="scada-region-row">
                  <span className="scada-region-label">By layer</span>
                  <span>
                    {Object.entries(region.by_layer || {}).map(([k, v]) => (
                      <span key={k} className="scada-pill scada-pill-neutral">{k}: {v}</span>
                    ))}
                  </span>
                </div>
              </div>
            </Section>

            <Section title="Highest-impact alerts">
              {region.top_alerts.map(a => (
                <div key={a.id} className="scada-alert-card" style={{ borderLeftColor: SEV_META[a.severity].color }}>
                  <div className="scada-alert-head">
                    <SeverityBadge severity={a.severity} />
                    <span className="scada-alert-code">{a.code}</span>
                    <span className="scada-impact-inline">
                      {(a.impact_value || a.impact_bbl_day || 0).toLocaleString()} {a.impact_unit || 'bbl/day'}
                    </span>
                  </div>
                  <div className="scada-alert-title">{a.asset_name}</div>
                  <div className="scada-alert-desc">{a.description}</div>
                </div>
              ))}
            </Section>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="scada-section">
      <div className="scada-section-title">{title}</div>
      <div className="scada-section-body">{children}</div>
    </div>
  );
}

function SeverityBadge({ severity }) {
  const meta = SEV_META[severity] || SEV_META.info;
  return (
    <span className="scada-sev-badge" style={{ background: meta.bg, color: meta.color }}>
      {meta.label}
    </span>
  );
}

export default SCADAPanel;
